import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle } from "lucide-react";
import { fdate } from "./LegalTabs";

/** Bandeau + bouton d'acceptation d'un document (CGV / CGU) pour un utilisateur connecté. */
export function LegalAcceptBar({ doc, status, onAccepted }) {
  const [busy, setBusy] = useState(false);
  const s = status?.[doc.key];
  if (!s?.acceptance_required) return null;
  const accept = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/legal/accept", { keys: [doc.key] });
      toast.success(`${doc.title} acceptées`);
      onAccepted?.(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  if (!s.needs_acceptance) {
    return <div className="mx-6 lg:mx-10 mt-5 border border-[#A7F3D0] bg-[#ECFDF5] text-[#065F46] text-xs px-4 py-3 flex items-center gap-2" data-testid={`legal-accepted-${doc.key}`}><CheckCircle2 className="h-4 w-4" />Version {s.accepted_version} acceptée le {fdate(s.accepted_at)}.</div>;
  }
  return (
    <div className="mx-6 lg:mx-10 mt-5 border-2 border-[#FFD700] bg-[#FFFBEB] px-4 py-3 flex flex-wrap items-center gap-3" data-testid={`legal-accept-bar-${doc.key}`}>
      <AlertTriangle className="h-4 w-4 text-[#B45309]" />
      <div className="text-xs flex-1 min-w-[200px]"><b>{s.accepted_version ? `Nouvelle version ${doc.version}` : "Acceptation requise"}</b> — merci de lire ce document puis de l'accepter.</div>
      <Button size="sm" onClick={accept} disabled={busy} data-testid={`legal-accept-${doc.key}`} className="h-9 bg-[#0A0A0A] text-white">{busy ? "…" : "J'ai lu et j'accepte"}</Button>
    </div>
  );
}

/** Hook : statut d'acceptation de l'utilisateur connecté, indexé par clé de document. */
export function useLegalStatus() {
  const [status, setStatus] = useState({});
  const [pending, setPending] = useState([]);
  const apply = (data) => {
    const map = {};
    (data?.documents || []).forEach((d) => { map[d.key] = d; });
    setStatus(map);
    setPending(data?.pending || []);
  };
  useEffect(() => { api.get("/legal/status").then((r) => apply(r.data)).catch(() => {}); }, []);
  return { status, pending, apply };
}
